if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      if (this.form) {
        this.form.querySelector('[name="id"]').disabled = false;
        this.cartDrawer = document.querySelector('cart-drawer');
        this.submitBtn = this.querySelector('[name="add"]');

        if (theme.settings.afterAtc !== 'no-js') {
          this.addEventListener('submit', this.handleSubmit.bind(this));
        }
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (evt.target.id === 'product-signup_form') return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      if (theme.settings.vibrateOnATC && window.navigator.vibrate) {
        window.navigator.vibrate(100);
      }

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');

      const formData = new FormData(this.form);
      let sections = 'cart-icon-bubble';
      if (this.cartDrawer) {
        sections += `,${this.cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        let error = typeof data.description === 'string' ? data.description : data.message;
        if (data.errors && typeof data.errors === 'object') {
          error = Object.entries(data.errors).map((item) => item[1].join(', '));
        }

        if (data.status && error) {
          this.setErrorMsgState(error);
        }

        // Fetch new cart state to verify if items were actually added (e.g. capped to stock availability)
        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        const newCartData = newCartResponse.ok ? await newCartResponse.json() : null;

        const itemsWereAdded = Boolean(
          newCartData && (
            newCartData.item_count > oldCartData.item_count ||
            newCartData.items.length > oldCartData.items.length ||
            JSON.stringify(newCartData.items.map((i) => ({ id: i.id, q: i.quantity }))) !==
              JSON.stringify(oldCartData.items.map((i) => ({ id: i.id, q: i.quantity })))
          )
        );

        // If no items were added and response failed, show error on form and throw
        if (!itemsWereAdded && !response.ok) {
          if (data.status && error) this.setErrorMsgState(error);
          throw new Error(error || response.status);
        }

        // If items were added but response was 422 (quantity capped), data.sections is not returned by Shopify.
        // Fetch rendered sections directly via Shopify cart update endpoint using sections_url.
        const drawerSection = this.cartDrawer ? this.cartDrawer.closest('.shopify-section') : null;
        const drawerSectionId = drawerSection ? drawerSection.id.replace('shopify-section-', '') : 'cart-drawer';
        const requestSections = ['cart-icon-bubble', 'cart-drawer', drawerSectionId].filter(Boolean);
        const sectionsParam = [...new Set(requestSections)].join(',');

        if (!data.sections || !Object.keys(data.sections).some((k) => k.includes('cart-drawer') || k === drawerSectionId)) {
          // 1. Primary method: POST to /cart/update with sections and sections_url
          try {
            const updateFormData = new FormData();
            updateFormData.append('sections', sectionsParam);
            updateFormData.append('sections_url', window.location.pathname);

            const cartUpdateUrl = (window.theme && theme.routes && theme.routes.cartUpdate)
              ? theme.routes.cartUpdate
              : '/cart/update';

            const updateRes = await fetch(cartUpdateUrl, {
              method: 'POST',
              headers: {
                Accept: 'application/javascript',
                'X-Requested-With': 'XMLHttpRequest'
              },
              body: updateFormData
            });

            if (updateRes.ok) {
              const updateData = await updateRes.json();
              if (updateData.sections) {
                data.sections = Object.assign({}, data.sections, updateData.sections);
              }
            }
          } catch (e) {
            console.error('Failed to fetch cart sections via update:', e);
          }

          // 2. Fallback method: GET request to current pathname or cart
          if (!data.sections || !Object.keys(data.sections).some((k) => k.includes('cart-drawer') || k === drawerSectionId)) {
            try {
              const pageUrl = window.location.pathname.includes('/cart') ? '/' : window.location.pathname;
              const pageRes = await fetch(`${pageUrl}?sections=${sectionsParam}`);
              if (pageRes.ok) {
                const pageData = await pageRes.json();
                if (pageData) {
                  data.sections = Object.assign({}, data.sections, pageData);
                }
              }
            } catch (e) {
              console.error('Failed to fetch cart sections via page GET:', e);
            }
          }
        }

        // Ensure variant_id and quantity are present on data
        const formVariantId = Number(formData.get('id'));
        if (!data.variant_id) {
          data.variant_id = formVariantId;
        }
        if (newCartData) {
          const addedItem = newCartData.items.find((i) => i.variant_id === data.variant_id);
          if (addedItem) {
            data.quantity = addedItem.quantity;
            // Also sync quantity inputs on the form if they were capped
            const qtyInput = this.querySelector('input[name="quantity"]');
            if (qtyInput) qtyInput.value = addedItem.quantity;
            const qtyComponent = this.querySelector('quantity-input');
            if (qtyComponent) qtyComponent.currentQty = addedItem.quantity;
          }
        }

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // If item was added from Quick Add drawer, update cart contents first,
          // then show "Added to cart" message and open cart drawer with fresh data.
          const quickAddDrawer = this.closest('quick-add-drawer');
          this.cartDrawer = this.cartDrawer || document.querySelector('cart-drawer');

          if (quickAddDrawer && this.cartDrawer) {
            await this.cartDrawer.renderContents(data, false);
            if (error) {
              const cartErrors = this.cartDrawer.querySelector('#cart-errors');
              if (cartErrors) {
                cartErrors.textContent = error;
                cartErrors.hidden = false;
              }
            }
            const delay = error ? 1200 : 0;
            setTimeout(() => {
              quickAddDrawer.addedToCart();
              // Safety timeout: ensure cart drawer opens even if quickAddDrawer.addedToCart() was delayed
              setTimeout(() => {
                if (this.cartDrawer.getAttribute('aria-hidden') === 'true' || !this.cartDrawer.hasAttribute('open')) {
                  if (typeof handleFloCartBtn === 'function') {
                    handleFloCartBtn();
                  } else {
                    this.cartDrawer.open();
                  }
                }
              }, 300);
            }, delay);
          } else {
            if (quickAddDrawer) quickAddDrawer.addedToCart();

            if (this.cartDrawer) {
              await this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
              if (error) {
                const cartErrors = this.cartDrawer.querySelector('#cart-errors');
                if (cartErrors) {
                  cartErrors.textContent = error;
                  cartErrors.hidden = false;
                }
              }
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }
        }

        // Notify other theme components that cart has updated
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));

        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));
      } finally {
        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
        }, 1400);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      if (cartIconBubble && response && response.sections && response.sections['cart-icon-bubble']) {
        const html = response.sections['cart-icon-bubble'];
        const tmpl = document.createElement('template');
        tmpl.innerHTML = html;
        const bubble = tmpl.content.querySelector('.shopify-section') || tmpl.content;
        cartIconBubble.innerHTML = bubble.innerHTML || html;
      }
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) {
        this.errorMsg.innerHTML = '';
        const errorArray = Array.isArray(error) ? error : [error];
        errorArray.forEach((err, index) => {
          if (index > 0) this.errorMsg.insertAdjacentHTML('beforeend', '<br>');
          this.errorMsg.insertAdjacentText('beforeend', err);
        });
      }
    }
  }

  customElements.define('product-form', ProductForm);
}
